import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { Bar, BarChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import ViewAllButton from 'src/components/ViewAllButton';
import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetGatewayStatistic } from 'src/features/statistic/api/useGetGatewayStatistic';
import MapLegend from 'src/features/statistic/components/MapLegend';
import StatisticTooltip from 'src/features/statistic/components/StatisticTooltip';
import { DataKey, TransformedBarItem } from 'src/features/statistic/types';
import { colorGatewayStatisticsMapping } from 'src/features/statistic/utils/constants';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useListZones } from 'src/features/zone/api/useListZones';
import { AdministrativeTranslation } from 'src/features/zone/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { tableMessages } from 'src/registry/components/table';
import { Button } from 'src/registry/ui/Button';
import DatePicker from 'src/registry/ui/DatePicker';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { ICON_MAP } from 'src/utils/constants';
import { safeFormatMessage } from 'src/utils/intl';

type CustomShapeProps = {
    dataKeys: DataKey[];
    payload?: TransformedBarItem;
    dataKey: string;
};

type SelectedZone = {
    name: string;
    id: string;
};

const CustomShape = (props: CustomShapeProps) => {
    const { payload, dataKey, dataKeys } = props;
    const lastKey = dataKeys.findLast(
        ({ value }) => payload && Object.keys(payload).includes(value) && !!payload[value],
    )?.value;

    return <Rectangle {...props} radius={lastKey === dataKey ? [0, 6, 6, 0] : undefined} />;
};

export default function VerticalBarChart({ date }: { date: dayjs.Dayjs }) {
    const { formatMessage, locale } = useIntl();
    const [zone, setZone] = React.useState<SelectedZone | null>(null);
    const accessGatewayPasses = useHasPermissions([PERMISSION.ACCESS_GATEWAY_PASSES]);

    const [dateTime, setDateTime] = React.useState({
        date,
        time: date.format('HH:mm'),
    });

    const { parkingId } = useParking();
    const { data, isLoading } = useGetGatewayStatistic({
        from_datetime: dateTime.date.format('YYYY-MM-DD'),
        zone: zone?.id ?? null,
        parkingId,
    });

    const params = new URLSearchParams();
    params.set(
        'filters',
        JSON.stringify([
            {
                id: 'time',
                value: {
                    afterDate: dateTime.date.startOf('day').format('YYYY-MM-DDTHH:mm'),
                    beforeDate: dateTime.date.endOf('day').format('YYYY-MM-DDTHH:mm'),
                },
            },
            ...(zone
                ? [
                      {
                          id: 'entry_zone',
                          value: [zone?.id],
                      },
                  ]
                : []),
        ]),
    );

    const { data: zoneData } = useListZones({ parkingId });

    const translate = React.useCallback(
        (translations?: AdministrativeTranslation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const { newData, dataKeys } = React.useMemo((): {
        dataKeys: DataKey[];
        newData: TransformedBarItem[];
    } => {
        if (!data)
            return {
                dataKeys: [],
                newData: [],
            };

        const dataKeys: DataKey[] = Array.from(
            Object.values(data.bars_section_stats).reduce((acc, currentValue) => {
                Object.keys(currentValue).map((key) => acc.add(key));

                return acc;
            }, new Set<string>()),
        ).map((key) => ({
            title: safeFormatMessage(formatMessage, key.replaceAll('_', '-'), key),
            value: key,
        }));

        const newData: TransformedBarItem[] = Object.entries(data.bars_section_stats).map(([key, item]) => ({
            ...item,
            title: safeFormatMessage(formatMessage, key.replaceAll('_', '-')),
        }));

        return {
            dataKeys,
            newData,
        };
    }, [data, formatMessage]);

    const singleDatePickerDate = new Date(dateTime.date.toISOString());
    const buttonDateFormat = singleDatePickerDate.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: '2-digit',
    });

    return (
        <GeneralBlock className="min-h-200 w-full gap-8 overflow-x-auto">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-row gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="text-sm">
                                <span className="max-w-30 truncate">
                                    {zone?.name ?? formatMessage(statisticMessages.allZones)}
                                </span>
                                <FontAwesomeIcon icon={ICON_MAP.CHEVRON_DOWN} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuLabel>{formatMessage(statisticMessages.chooseZone)}</DropdownMenuLabel>
                            {(zoneData?.results?.length || 0) > 0 && <DropdownMenuSeparator />}
                            {zoneData?.results.map((z) => (
                                <DropdownMenuCheckboxItem
                                    checked={z.id === zone?.id}
                                    onSelect={() => {
                                        setZone({
                                            id: z.id,
                                            name: translate(z.translations),
                                        });
                                    }}
                                >
                                    {translate(z.translations)}
                                </DropdownMenuCheckboxItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItemWithIcon
                                variant="destructive"
                                icon={ICON_MAP.DELETE}
                                onSelect={() => setZone(null)}
                            >
                                {formatMessage(tableMessages.clearFilter)}
                            </DropdownMenuItemWithIcon>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <DatePicker
                        value={singleDatePickerDate}
                        buttonValue={buttonDateFormat}
                        onChange={(date) => setDateTime({ ...dateTime, date: dayjs(date) })}
                    />
                </div>

                <div>
                    {accessGatewayPasses && params && (
                        <ViewAllButton route={`/${PATH.VISITORS}/gateway-passes?${params?.toString()}`} />
                    )}
                </div>
            </div>

            <div className="flex flex-row items-center justify-center gap-8">
                <div className="flex flex-col gap-2">
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.passes)}
                        value={data?.metrics_section_stats.total_passes_count}
                    />
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.detections)}
                        value={data?.metrics_section_stats.detections_count}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.failedGatewayPasses)}
                        value={data?.metrics_section_stats.failed_passes_count}
                    />
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.vehiclesPerHour)}
                        value={data?.metrics_section_stats.vehicles_per_hour_count}
                    />
                </div>
            </div>
            <ResponsiveContainer>
                <BarChart data={newData} layout="vertical">
                    <XAxis type="auto" hide />
                    <YAxis
                        type="category"
                        dataKey="title"
                        tickMargin={8}
                        interval={0}
                        width={100}
                        tick={({ x, y, payload }) => (
                            <text x={x} y={y} dy={4} textAnchor="end" className="text-xs">
                                {payload.value}
                            </text>
                        )}
                    />
                    <Tooltip content={(props) => <StatisticTooltip {...props} />} />
                    {dataKeys.map(({ value }) => (
                        <Bar
                            stackId="statistic"
                            key={value}
                            dataKey={value}
                            background
                            fill={colorGatewayStatisticsMapping && value && colorGatewayStatisticsMapping[value]}
                            shape={(props) => <CustomShape dataKeys={dataKeys} dataKey={value} {...props} />}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <MapLegend colorMapping={colorGatewayStatisticsMapping} className="flex-wrap gap-2" />
        </GeneralBlock>
    );
}
